import React, { forwardRef, useEffect, useState, useRef, useMemo } from 'react'
import PropTypes, { number } from 'prop-types'
import { Button, DatePicker, Modal, Form, Input, InputNumber, Select, Tag, Upload, message, Space, Switch, Table, Tooltip, Checkbox } from 'antd'
import { MinusCircleOutlined, InboxOutlined, CloseOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { Col, Grid, Row } from 'react-flexbox-grid'
import * as Constants from '../../../constants'
import { storage, cloudFunctions, db, firestore } from '../../../config/Fire'
import './styles.css'
const { Dragger } = Upload;

const { Option } = Select

const FormBidOrder = forwardRef((props, ref) => {
    const { loading, onSubmitBidOrder, isEditing, bidOrderToEdit, clients, outsourcing, companyType } = props
    const [form] = Form.useForm()
    const [modalForm] = Form.useForm()
    const [categorySelected, setCategorySelected] = useState([])
    const [companySelected, setCompanySelected] = useState([])
    const [clientsFiltered, setClientsFiltered] = useState([])
    const [countrySelected, setCountrySelected] = useState(null)
    const [countryOptions] = useState([
        { id: 1, nombre: 'Argentina' },
        { id: 2, nombre: 'Uruguay' }
    ])
    const [provinceOptions, setProvinceOptions] = useState([])
    const [citiesOptions, setCitiesOptions] = useState([])
    const [provinceSelected, setProvinceSelected] = useState([])
    const [outsourcingFiltered, setOutsourcingFiltered] = useState([])
    const [hasTopography, setHasTopography] = useState(false)
    const [bidOrderIsSAT, setBidOrderIsSAT] = useState(false)
    const [fileUrl, setFileUrl] = useState(null)
    const [fileName, setFileName] = useState(null)
    const [items, setItems] = useState([])
    const [itemName, setItemName] = useState('')
    const [itemDescription, setItemDescription] = useState('')
    const [itemPrice, setItemPrice] = useState()
    const [itemPriceUsd, setItemPriceUsd] = useState()
    const [itemDays, setItemDays] = useState()
    const [bnaSellRate, setBnaSellRate] = useState(null)
    const [bnaSellRateLoading, setBnaSellRateLoading] = useState(false)
    const draggerProps = {
        name: 'file',
        action: (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => resolve(reader.result);
                reader.onerror = error => reject(error);
            })
        },
        headers: {
            Authorization: 'Access-Control-Allow-Origin'
        },
        accept: '.pdf',
        onChange(info) {
            const { status } = info.file;
            if (status === 'done') {
                message.success(`${info.file.name} file uploaded successfully.`);
                setFileUrl(info.file)
            } else if (status === 'error') {
                message.error(`${info.file.name} file upload failed.`);
            }
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
    };
    const [apiValues, setApiValues] = useState([])
    const linkFileRef = useRef(null)
    const isInitialLoadRef = useRef(true)
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalClientVisible, setIsModalClientVisible] = useState(false);
    const [modalFormClient] = Form.useForm()

    useEffect(() => {
        if (clients) {
            const usersToFilter = Object.values(clients)
            setClientsFiltered(usersToFilter)
        }
    }, [clients])

    useEffect(() => {
        if (outsourcing) {
            const val = Object.values(outsourcing)
            setOutsourcingFiltered(val)
        }
    }, [outsourcing])

    useEffect(() => {
        // Establecer empresa por defecto si no está editando y hay companyType
        if (!isEditing && companyType) {
            form.setFieldsValue({ company: companyType })
            asignCompanyAndChangeCategory(companyType)
        }
    }, [companyType, isEditing])

    useEffect(() => {
        // Si está editando, establecer país por defecto o desde bidOrderToEdit
        if (isEditing && bidOrderToEdit) {
            const country = bidOrderToEdit.country || 'Argentina' // Retrocompatibilidad
            setCountrySelected(country)
            getProvinceOptions(country).then(() => {
                // Después de cargar provincias, establecer provincia seleccionada si existe
                if (bidOrderToEdit.province) {
                    setProvinceSelected(bidOrderToEdit.province)
                }
            })
            // Cargar valor de SAT si existe
            setBidOrderIsSAT(bidOrderToEdit.SAT !== undefined ? bidOrderToEdit.SAT : false)
        } else {
            // Por defecto Argentina para nuevas cotizaciones
            setCountrySelected('Argentina')
            getProvinceOptions('Argentina')
        }
        if (isEditing) {
            if (bidOrderToEdit.fileUrl) {
                const stringSplit = bidOrderToEdit.fileUrl.split('/')
                const fileName = stringSplit[stringSplit.length - 1]
                setFileName(fileName)
                storage.ref(bidOrderToEdit.fileUrl).getDownloadURL().then(function(url) {
                    setFileUrl(url)
                }).catch(function(error) {
                    console.log(error)
                });
            }
            // Cargar items si existen
            if (bidOrderToEdit.items && Array.isArray(bidOrderToEdit.items)) {
                // Los items ya deberían tener id desde Firestore, pero por seguridad asignamos si falta
                const itemsWithId = bidOrderToEdit.items.map((item, index) => ({
                    ...item,
                    id: item.id || index + 1, // Si no tiene id, asignar secuencial
                    // Retrocompatibilidad: asegurar tipo numérico y campo USD opcional
                    price: typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0,
                    priceUsd: typeof item.priceUsd === 'number'
                        ? item.priceUsd
                        : item.priceUsd
                            ? parseFloat(item.priceUsd) || null
                            : null
                }))
                setItems(itemsWithId)
            }
        }
        // Marcar que la carga inicial ha terminado después de un breve delay
        const timer = setTimeout(() => {
            isInitialLoadRef.current = false
        }, 500)
        return () => clearTimeout(timer)
    }, [])

    const downloadFile = () => {
        storage.ref().child(bidOrderToEdit.fileUrl).getDownloadURL().then(url => {
            if(linkFileRef && linkFileRef.current) {
                linkFileRef.current.href = url
                linkFileRef.current.click();
                linkFileRef.current.remove();
            }
        })
    }

    const getProvinceOptions = async (country = 'Argentina') => {
        try {
            const apiFile = country === 'Uruguay' ? 'api-uruguay.json' : 'api.json'
            const res = await fetch(apiFile)
            const data = await res.json()
            setApiValues(data)
            if (data) {
                let arr = data.map((province) => {
                    return {
                        id: province.id,
                        nombre: province.nombre,
                    }
                })
                setProvinceOptions(arr)
            }
            return Promise.resolve()
        } catch (e) {
            console.error("Error getting provincies: ", e)
            return Promise.reject(e)
        }
    }

    useEffect(() => {
        if (countrySelected && !isInitialLoadRef.current) {
            // Solo ejecutar si no es la carga inicial
            getProvinceOptions(countrySelected)
            // Limpiar provincia y ciudad cuando el usuario cambia el país manualmente
            setProvinceSelected(null)
            setCitiesOptions([])
            form.setFieldsValue({ province: undefined, city: undefined })
        }
    }, [countrySelected])

    useEffect(() => {
        getCitiesOptions()
    }, [provinceSelected])

    const getCitiesOptions = async () => {
        if (provinceSelected) {
            for (let i = 0; i < apiValues.length; i++) {
                if (apiValues[i].nombre === provinceSelected) {
                    let arr = apiValues[i].ciudades.map((city) => {
                        return {
                            id: city.id,
                            nombre: city.nombre,
                        };
                    });
                    if (provinceSelected === "Santa Fe") {
                        let customIds = apiValues.length
                        customIds++
                        arr.push({ id: customIds, nombre: "Nicanor Molina" });
                        customIds++
                        arr.push({ id: customIds, nombre: "Colonia La Nueva" });
                        customIds++
                        arr.push({ id: customIds, nombre: "Colonia San José" });
                    }
                    setCitiesOptions(arr);
                }
            }
                
        }
    }

    const filterClients = value => {
        setClientsFiltered([])
        if (clients) {
            const valueLowerCase = value.toLowerCase()
            const clientsFinded = Object.values(clients).filter(client => client.email.toLowerCase().includes(valueLowerCase) ||
                (client.name !== undefined && client.name.toLowerCase().includes(valueLowerCase)))
            setClientsFiltered(clientsFinded)
        }
    }

    const filteroutsourcing = value => {
        setOutsourcingFiltered([])
        if (outsourcing) {
            const valueLowerCase = value.toLowerCase()
            const outsourcingFinded = Object.values(outsourcing).filter(outsourcing => outsourcing.email.toLowerCase().includes(valueLowerCase) ||
                (outsourcing.name !== undefined && outsourcing.name.toLowerCase().includes(valueLowerCase)))
            setOutsourcingFiltered(outsourcingFinded)
        }
    }

    const asignCompanyAndChangeCategory = (company) => {
        setCompanySelected(company)
        form.setFieldsValue({ category: undefined })
        setCategorySelected([])
    }

    const clearBidOrder = () => {
        form.resetFields()
        setItems([])
        setItemName('')
        setItemDescription('')
        setItemPrice()
        setItemPriceUsd()
        setItemDays()
    }

    const showModal = () => {
        setIsModalVisible(true);
    };
    
    const handleOk = () => {
        setIsModalVisible(false);
        modalForm.resetFields()
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        modalForm.resetFields()
    };

    const handleFinish = (values) => {
        cloudFunctions.newOutsourcing(values)
            .then(response => {
                if (response.data && response.data.code !== undefined && response.data.code === 0 && response.data.outsourcedOrder !== undefined) {
                    message.success('¡El proveedor se guardó exitósamente!')
                    outsourcingFiltered.push(response.data.outsourcedOrder)
                    modalForm.resetFields()
                    setOutsourcingFiltered(outsourcingFiltered)
                    document.getElementById('outsourcingSelect').dataset.value = outsourcingFiltered
                } else {
                    console.log(response)
                    message.error('Se produjo un error al guardar el proveedor. Por favor, vuelva a intentarlo')
                }
            })
            .catch(err => {
                console.log(err)
                message.error('Se produjo un error al guardar el proveedor. Por favor, vuelva a intentarlo')
            })
    };

    const showModalClient = () => {
        setIsModalClientVisible(true);
    }

    const handleOkClient = () => {
        setIsModalClientVisible(false);
        modalFormClient.resetFields()
    }

    const handleCancelClient = () => {
        setIsModalClientVisible(false);
        modalFormClient.resetFields()
    }

    const handleFinishClient = (values) => {
        try {
            values.type = values.type === undefined ? false : values.type
            const client = values
            const newClientKey = db.ref().child(Constants.MAIN_NODES.USERS).push().key
            client.id = newClientKey
            client.userType = Constants.USER_TYPES.CLIENT
            const date = new Date()
            const dateStr = `${date.getDate().toString().length === 1 ? '0' + date.getDate() : date.getDate()}/${(date.getMonth() + 1).toString().length === 1 ? '0' + (date.getMonth() + 1) : (date.getMonth() + 1)}/${date.getFullYear()} ${date.getHours()}:${date.getMinutes()}`
            client.createDate = dateStr
            db.ref(`${Constants.MAIN_NODES.USERS}/${newClientKey}`).set(client)
            message.success('El cliente se guardo exitósamente')
            clientsFiltered.push(client)
            setClientsFiltered(clientsFiltered)
            clients[newClientKey] = client
        } catch (err) {
            console.log(err)
            message.error('Se produjo un error al guardar el cliente. Por favor, vuelva a intentarlo')
        }
    }

    const deleteFile = () => {
        storage.ref().child(bidOrderToEdit.fileUrl).delete().then(() => {
            setFileName(null)
            message.success('Se eliminó el archivo correctamente')
        }).catch(err => {
            console.log(err)
            message.error('Se produjo un error al eliminar el archivo. Por favor, vuelva a intentarlo')
        })
        // delete from firestore
        firestore.collection(Constants.MAIN_NODES.BID_ORDERS).doc(bidOrderToEdit.id).update({ fileUrl: null })
            .then(() => {
            })
            .catch(err => {
                console.log(err)
                message.error('Se produjo un error al eliminar el archivo. Por favor, vuelva a intentarlo')
            })
    }

    const onChangeSwitch = (checked) => {
        modalFormClient.setFieldsValue({ type: checked })
    }

    const getInitialValuesFromWorkOrder = () => {
        // Retrocompatibilidad: si no tiene country, asumir Argentina
        const country = bidOrderToEdit.country || 'Argentina'
        return {
            ...bidOrderToEdit,
            country: country,
            hasTopography: bidOrderToEdit.hasTopography === true ? 'Pendiente de viaje' : bidOrderToEdit.hasTopography === false ? 'No contempla' : bidOrderToEdit.hasTopography,
            SAT: bidOrderToEdit.SAT !== undefined ? bidOrderToEdit.SAT : false,
        }
    }

    // Obtener cotización BNA venta al montar (vía Cloud Function para evitar CORS)
    useEffect(() => {
        const fetchRate = async () => {
            try {
                setBnaSellRateLoading(true)
                const response = await cloudFunctions.getBnaSellRate({})
                if (response && response.data && response.data.code === 0 && response.data.data && response.data.data.sellRate) {
                    setBnaSellRate(response.data.data.sellRate)
                } else {
                    setBnaSellRate(null)
                }
            } catch (e) {
                console.log(e)
                setBnaSellRate(null)
            } finally {
                setBnaSellRateLoading(false)
            }
        }
        fetchRate()
    }, [])

    // Calcular automáticamente el precio USD del ítem en edición cuando cambian pesos o cotización
    useEffect(() => {
        if (bnaSellRate && itemPrice && itemPrice > 0) {
            const usd = parseFloat((itemPrice / bnaSellRate).toFixed(2))
            setItemPriceUsd(usd)
        } else {
            setItemPriceUsd(null)
        }
    }, [itemPrice, bnaSellRate])

    const handleAddItem = () => {
        if (!itemName.trim() || !itemDescription.trim() || itemPrice <= 0) {
            message.error('Complete todos los campos del item correctamente')
            return
        }
        // Calcular el siguiente id secuencial (1, 2, 3, ...)
        const nextId = items.length > 0 ? Math.max(...items.map(item => item.id || 0)) + 1 : 1
        const priceNumber = parseFloat(itemPrice) || 0
        const priceUsdNumber = itemPriceUsd ? parseFloat(itemPriceUsd) : (bnaSellRate ? parseFloat((priceNumber / bnaSellRate).toFixed(2)) : null)
        const newItem = {
            id: nextId, // ID secuencial que se guardará en Firestore
            name: itemName.trim(),
            description: itemDescription.trim(),
            price: priceNumber,
            priceUsd: priceUsdNumber,
            days: itemDays ? parseInt(itemDays) : null
        }
        setItems([...items, newItem])
        setItemName('')
        setItemDescription('')
        setItemPrice()
        setItemPriceUsd()
        setItemDays()
        message.success('Item agregado correctamente')
    }

    const handleDeleteItem = (itemId) => {
        setItems(items.filter(item => item.id !== itemId))
        message.success('Item eliminado correctamente')
    }

    // Totales calculados en base a los items
    const totals = useMemo(() => {
        const totalPesos = items.reduce((sum, item) => {
            const value = item && item.price !== undefined ? parseFloat(item.price) || 0 : 0
            return sum + value
        }, 0)
        const totalUsd = items.reduce((sum, item) => {
            const value = item && item.priceUsd !== undefined && item.priceUsd !== null ? parseFloat(item.priceUsd) || 0 : 0
            return sum + value
        }, 0)
        const totalDays = items.reduce((sum, item) => {
            const value = item && item.days ? parseInt(item.days) || 0 : 0
            return sum + value
        }, 0)
        return {
            totalPesos,
            totalUsd,
            totalDays,
        }
    }, [items])

    // Interceptar el submit del formulario para incluir items y totales
    const handleFormSubmit = (values) => {
        let amountPesos
        let amountDollars
        const totalDays = totals.totalDays

        if (items.length > 0) {
            // Cuando hay ítems, los totales se calculan automáticamente
            amountPesos = parseFloat(totals.totalPesos.toFixed(2))
            amountDollars = parseFloat(totals.totalUsd.toFixed(2))
        } else {
            // Compatibilidad con registros viejos sin ítems: usar los montos del formulario
            amountPesos = values.amountPesos ? parseFloat(values.amountPesos) : 0
            amountDollars = values.amountDollars ? parseFloat(values.amountDollars) : 0
        }

		const parsedEngineeringDays =
			values.quotedEngineeringDays !== undefined &&
			values.quotedEngineeringDays !== null &&
			values.quotedEngineeringDays !== ''
				? parseFloat(values.quotedEngineeringDays)
				: null

		const parsedPredictedKilometers =
			values.predictedKilometers !== undefined &&
			values.predictedKilometers !== null &&
			values.predictedKilometers !== ''
				? parseInt(values.predictedKilometers, 10)
				: null

		const formValuesWithItems = {
			...values,
			quotedEngineeringDays: !Number.isNaN(parsedEngineeringDays) ? parsedEngineeringDays : null,
			predictedKilometers: !Number.isNaN(parsedPredictedKilometers) ? parsedPredictedKilometers : null,
			items: items,
			amountPesos,
			amountDollars,
			totalDays,
		}
        onSubmitBidOrder(formValuesWithItems)
    }

    return (
        <Grid>
            <Form form={form} onFinish={handleFormSubmit} initialValues={isEditing && getInitialValuesFromWorkOrder()}>
                <Row>
                    <Col xs={12} md={3}>
                        <Form.Item name="numeration" initialValue={"X"}>
                            <Input prefix="Numeración:" style={{ width: '100%' }} disabled/>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="quotation" initialValue={"XX-XXXX-XX-X"}>
                            <Input prefix="Cotización:" style={{ width: '100%' }} disabled/>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="company" rules={[{ required: true, message: 'Seleccione una empresa' }]}>
                            <Select placeholder="Empresa" onChange={company => asignCompanyAndChangeCategory(company)} disabled={isEditing}>
                                {
                                    Constants.companyBidOrderMap && Object.keys(Constants.companyBidOrderMap).map(key =>
                                        <Option key={Constants.companyBidOrderMap[key]}
                                            value={Constants.companyBidOrderMap[key]}>{Constants.companyBidOrderMap[key]}
                                        </Option>)
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="category" rules={[{ required: true, message: 'Seleccione un rubro' }]}>
                            <Select placeholder="Rubro" onChange={category => setCategorySelected(category)} disabled={isEditing}>
                                {
                                    companySelected === 'Arcasa' ?
                                    Constants.categoryARCASABidOrderMap && Object.keys(Constants.categoryARCASABidOrderMap).map(key =>
                                        <Option key={Constants.categoryARCASABidOrderMap[key]}
                                            value={Constants.categoryARCASABidOrderMap[key]}>{Constants.categoryARCASABidOrderMap[key]}
                                        </Option>)
                                    : companySelected === 'SD' ?
                                    Constants.categorySDBidOrderMap && Object.keys(Constants.categorySDBidOrderMap).map(key =>
                                        <Option key={Constants.categorySDBidOrderMap[key]}
                                            value={Constants.categorySDBidOrderMap[key]}>{Constants.categorySDBidOrderMap[key]}
                                        </Option>)
                                    : null
                                }
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Row>
                            <Col xs={8}>
                                <Form.Item name="clients">
                                    <Select
                                        mode="multiple"
                                        maxTagCount={1}
                                        placeholder="Comitente/s"
                                        filterOption={false}
                                        onSearch={filterClients}
                                        style={{ width: '100%' }}>
                                        {clientsFiltered.map(d => (
                                            d.enabled ?
                                                <Option
                                                    key={d.id} value={d.id}>{`${d.name}`}</Option>
                                                :
                                                <Option disabled
                                                        key={d.id} value={d.id}>{`${d.name}`}&nbsp;
                                                    <Tag icon={<MinusCircleOutlined/>} color="red">Deshabilitado</Tag></Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Form.Item name="newClient">
                                <Button onClick={showModalClient}>Agregar cliente</Button>
                            </Form.Item>
                        </Row>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="proyect" rules={[{ required: true, message: 'Ingrese nombre del proyecto' }]}>
                            <Input placeholder="Proyecto"/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={3}>
                        <Form.Item name="startDate" rules={[{ required: true, message: 'Ingrese fecha' }]}>
                            <DatePicker format={'DD/MM/YYYY'} placeholder="Fecha" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="country" rules={[{ required: true, message: 'Seleccione un país' }]}>
                            <Select placeholder="País" onChange={country => {
                                setCountrySelected(country)
                            }}>
                                {countryOptions.map(d => (
                                    <Option key={d.id} value={d.nombre}>{d.nombre}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="province" rules={[{ required: true, message: 'Seleccione una provincia' }]}>
                            <Select
                                showSearch
                                placeholder="Provincia"
                                optionFilterProp="value"
                                onChange={province => setProvinceSelected(province)}
                                filterOption={(input, option) =>
                                    option && option.value && option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {provinceOptions.map(d => (
                                    <Option key={d.id} value={d.nombre}>{d.nombre}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={3}>
                        <Form.Item name="city" rules={[{ required: true, message: 'Seleccione una ciudad' }]}>
                            <Select
                                showSearch
                                placeholder="Ciudad"
                                optionFilterProp="value"
                                filterOption={(input, option) =>
                                    option && option.value && option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {citiesOptions.map(d => (
                                    <Option key={d.id} value={d.nombre}>{d.nombre}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                {
                    items.length === 0 && isEditing && (
                        <Row>
                            <Col xs={12} md={4}>
                                <Form.Item name="billing" rules={[{ required: true, message: 'Seleccione un tipo de facturación' }]}>
                                    <Select placeholder="Facturación">
                                        {
                                            Constants.billingBidOrderMap && Object.keys(Constants.billingBidOrderMap).map(key =>
                                                <Option key={Constants.billingBidOrderMap[key]}
                                                    value={Constants.billingBidOrderMap[key]}>{Constants.billingBidOrderMap[key]}
                                                </Option>)
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Item name="amountPesos" rules={[{ required: true, message: 'Ingrese monto en pesos' }]}>
                                    <Input type='number' prefix="$" placeholder="Monto final IVA incluido" style={{ width: '100%' }}/>
                                </Form.Item>
                            </Col>
                            <Col xs={12} md={4}>
                                <Form.Item name="amountDollars">
                                    <Input type='number' prefix="us$" placeholder="Monto dolares BNA venta" style={{ width: '100%' }}/>
                                </Form.Item>
                            </Col>
                        </Row>
                    )
                }
                <Row>
                    {
                        (items.length > 0 || !isEditing) && (
                            <Col xs={12} md={4}>
                                <Form.Item name="billing" rules={[{ required: true, message: 'Seleccione un tipo de facturación' }]}>
                                    <Select placeholder="Facturación">
                                        {
                                            Constants.billingBidOrderMap && Object.keys(Constants.billingBidOrderMap).map(key =>
                                                <Option key={Constants.billingBidOrderMap[key]}
                                                    value={Constants.billingBidOrderMap[key]}>{Constants.billingBidOrderMap[key]}
                                                </Option>)
                                        }
                                    </Select>
                                </Form.Item>
                            </Col>
                        )
                    }
                    <Col xs={12} md={items.length > 0 || !isEditing ? 4 : 6}>
                        <Form.Item name="paymentForm">
                            <Input placeholder="Forma de pago" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={12} md={items.length > 0 || !isEditing ? 4 : 6}>
                        <Form.Item name="paymentTerm" rules={[{ required: true, message: 'Ingrese plazo de entrega' }]}>
                            <Input type='number' placeholder="Plazo de entrega" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={6}>
                        <Row>
                            <Col xs={8}>
                            <Form.Item name="outsourcing">
                                <Select
                                    mode="multiple"
                                    maxTagCount={1}
                                    placeholder="Proveedor/es"
                                    filterOption={false}
                                    onSearch={filteroutsourcing}
                                    style={{ width: '100%' }}
                                    id='outsourcingSelect'
                                >
                                    {outsourcingFiltered.map(d => (
                                        <Option key={d.id} value={d.id}>{`${d.name}`}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            </Col>
                            <Form.Item name="new">
                                <Button onClick={showModal}>Agregar proveedor</Button>
                            </Form.Item>
                        </Row>
                    </Col>
                    <Col xs={12} md={6}>
                        <Form.Item name="outsourcingAmount">
                            <Input type='number' placeholder="Monto final IVA incluido" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row xs={12} md={6}>
                    <Col xs={4}>
                        <Form.Item name="predictedKilometers" style={{ textAlign: 'center' }}>
                            <Input type='number' placeholder="Kilómetros previstos" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={4}>
                        <Form.Item name="quotedEngineeringDays" style={{ textAlign: 'center' }}>
                            <Input type='number' placeholder="Días de ingeniería cotizados" style={{ width: '100%' }}/>
                        </Form.Item>
                    </Col>
                    <Col xs={4}>
                        <Form.Item name="SAT" valuePropName="checked" style={{ textAlign: 'center' }}>
                            <Checkbox checked={bidOrderIsSAT} onChange={value => setBidOrderIsSAT(value.target.checked)}>Derivado de SAT</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>
                <Row xs={12} md={6}>
                    <Col xs={6}>
                        <Form.Item name="hasTopography" style={{ textAlign: 'center' }} rules={[{ required: true, message: 'Seleccione topografia' }]}>
                            <Select placeholder="Seleccione el estado de la topografía" value={hasTopography}>
                                <Option value="No contempla">No contempla</Option>
                                <Option value="Pendiente de viaje">Pendiente de viaje</Option>
                                <Option value="Procesamiento en gabinete">Procesamiento en gabinete</Option>
                                <Option value="Terminada">Terminada</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={6}>
                        {
                            isEditing ?
                            <Form.Item name="fileItem" style={{ textAlign: 'center' }}>
                                <Row center='xs'>
                                    <Space>
                                        Cotización pdf:
                                        <div className='fileName' onClick={downloadFile}> {fileName}</div>
                                        {
                                            fileName ?
                                            <CloseOutlined onClick={() => deleteFile()} />
                                            : null
                                        }
                                    </Space>
                                </Row>
                            </Form.Item>
                            : null
                        }
                        {
                            fileName ?
                            <Form.Item name="fileItem" style={{ textAlign: 'center' }}>
                                <Dragger {...draggerProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">Clickea o arrastra un archivo para cargar</p>
                                </Dragger>
                            </Form.Item>
                            : 
                            <Form.Item name="fileItem" style={{ textAlign: 'center' }}>
                                <Dragger {...draggerProps}>
                                    <p className="ant-upload-drag-icon">
                                        <InboxOutlined />
                                    </p>
                                    <p className="ant-upload-text">Clickea o arrastra un archivo para cargar</p>
                                </Dragger>
                            </Form.Item>
                        }
                    </Col>
                    <a ref={linkFileRef} href="" target='_blank' download/>
                </Row>
                <Row center="xs" style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <Col xs={12}>
                        <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <h3 style={{ fontSize: '18px', margin: 0 }}>Items</h3>
                            <Tooltip title="Para guardar los cambios realizados en los ítems (agregar, eliminar), es necesario presionar el botón 'Guardar' o 'Actualizar'">
                                <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'help', fontSize: '16px' }} />
                            </Tooltip>
                        </div>
                        {bnaSellRate && (
                            <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px' }}>
                                Cotización BNA venta: <strong>US$ 1 = ${bnaSellRate.toFixed(2)}</strong>
                            </div>
                        )}
                    </Col>
                </Row>
                <Row>
                    <Col xs={12} md={2}>
                        <Input 
                            placeholder="Nombre del item"
                            value={itemName}
                            onChange={(e) => setItemName(e.target.value)}
                            style={{ marginBottom: '8px' }}
                        />
                    </Col>
                    <Col xs={12} md={3}>
                        <Input.TextArea 
                            placeholder="Descripción"
                            value={itemDescription}
                            onChange={(e) => setItemDescription(e.target.value)}
                            style={{ marginBottom: '8px' }}
                        />
                    </Col>
                    <Col xs={12} md={2}>
                        <InputNumber 
                            placeholder="Precio (ARS)" 
                            value={itemPrice}
                            onChange={(value) => setItemPrice(value || 0)}
                            style={{ width: '100%', marginBottom: '8px' }}
                            prefix="$"
                            min={0}
                            step={1}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                            formatter={(value) => value ? `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                            controls={true}
                        />
                    </Col>
                    <Col xs={12} md={2}>
                        <InputNumber
                            placeholder="Precio (USD)"
                            value={itemPriceUsd}
                            onChange={(value) => setItemPriceUsd(value || 0)}
                            style={{ width: '100%', marginBottom: '8px' }}
                            prefix="US$"
                            min={0}
                            step={0.01}
                            formatter={(value) => value ? `US$ ${value}` : ''}
                            controls={true}
                            disabled={true}
                        />
                    </Col>
                    <Col xs={12} md={2}>
                        <InputNumber 
                            placeholder="Plazo (días)" 
                            value={itemDays}
                            onChange={(value) => setItemDays(value || null)}
                            style={{ width: '100%', marginBottom: '8px' }}
                            min={0}
                            step={1}
                            parser={(value) => value.replace(/\D/g, '')}
                            formatter={(value) => value ? `${value}` : ''}
                            controls={true}
                        />
                    </Col>
                    <Col xs={12} md={1}>
                        <Button 
                            type="primary" 
                            onClick={handleAddItem}
                            style={{ marginBottom: '8px' }}
                        >
                            Agregar
                        </Button>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <Table
                            dataSource={items}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: 'Id',
                                    dataIndex: 'id',
                                    key: 'id',
                                    width: 80,
                                    align: 'center',
                                },
                                {
                                    title: 'Nombre',
                                    dataIndex: 'name',
                                    key: 'name',
                                    width: 150,
                                    ellipsis: true,
                                },
                                {
                                    title: 'Descripción',
                                    dataIndex: 'description',
                                    key: 'description',
                                    ellipsis: {
                                        showTitle: false,
                                    },
                                    render: (text) => (
                                        <span title={text} style={{ wordBreak: 'break-word' }}>
                                            {text}
                                        </span>
                                    ),
                                },
                                {
                                    title: 'Precio (ARS)',
                                    dataIndex: 'price',
                                    key: 'price',
                                    width: 120,
                                    render: (price) => `$ ${price?.toFixed(2) || '0.00'}`,
                                    align: 'right'
                                },
                                {
                                    title: 'Precio (USD)',
                                    dataIndex: 'priceUsd',
                                    key: 'priceUsd',
                                    width: 120,
                                    render: (priceUsd) => priceUsd ? `US$ ${priceUsd.toFixed(2)}` : '-',
                                    align: 'right'
                                },
                                {
                                    title: 'Plazo (días)',
                                    dataIndex: 'days',
                                    key: 'days',
                                    width: 120,
                                    render: (days) => days ? `${days} días` : '-',
                                    align: 'center'
                                },
                                {
                                    title: 'Acciones',
                                    key: 'actions',
                                    align: 'center',
                                    width: 120,
                                    render: (_, record) => (
                                        <Button 
                                            type="text" 
                                            danger 
                                            icon={<DeleteOutlined />}
                                            onClick={() => handleDeleteItem(record.id)}
                                        >
                                            Eliminar
                                        </Button>
                                    )
                                }
                            ]}
                            summary={() => (
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0}>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                        <strong>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">
                                        <strong>$ {totals.totalPesos.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4} align="right">
                                        <strong>US$ {totals.totalUsd.toFixed(2)}</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5} align="center">
                                        <strong>{totals.totalDays} días</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6}>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            )}
                        />
                    </Col>
                </Row>
                <Row center="xs" className='buttons'>
                    <Col xs={6}>
                        <Form.Item style={{ textAlign: 'center' }}>
                            <Button type="primary" htmlType="submit" loading={loading}>
                                {isEditing ? 'Actualizar' : 'Guardar'}
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col xs={6}>
                        <Form.Item style={{ textAlign: 'center' }}>
                            <Button onClick={clearBidOrder} loading={loading}>
                                {isEditing ? 'Restablecer' : 'Reiniciar formulario'}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Modal title="Agregar proveedor" visible={isModalVisible} onCancel={handleCancel} onOk={handleOk}>
                <Form form={modalForm} onFinish={handleFinish} onAbort={handleCancel}>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Item name="name" rules={[{ required: true, message: 'Por favor, ingrese razón social' }]}>
                                <Input placeholder="Razón social"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="category" rules={[{ required: true, message: 'Por favor, ingrese rubro' }]}>
                                <Select placeholder="Rubro" onChange={category => setCategorySelected(category)}>
                                    {
                                        companySelected === 'Arcasa' ?
                                        Constants.categoryARCASABidOrderMap && Object.keys(Constants.categoryARCASABidOrderMap).map(key =>
                                            <Option key={Constants.categoryARCASABidOrderMap[key]}
                                                value={Constants.categoryARCASABidOrderMap[key]}>{Constants.categoryARCASABidOrderMap[key]}
                                            </Option>)
                                        : companySelected === 'SD' ?
                                        Constants.categorySDBidOrderMap && Object.keys(Constants.categorySDBidOrderMap).map(key =>
                                            <Option key={Constants.categorySDBidOrderMap[key]}
                                                value={Constants.categorySDBidOrderMap[key]}>{Constants.categorySDBidOrderMap[key]}
                                            </Option>)
                                        : Constants.categorySDBidOrderMap && Object.keys(Constants.categorySDBidOrderMap).map(key =>
                                            <Option key={Constants.categorySDBidOrderMap[key]}
                                                value={Constants.categorySDBidOrderMap[key]}>{Constants.categorySDBidOrderMap[key]}
                                            </Option>)
                                    }
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="phone" rules={[{ required: true, message: 'Por favor, ingrese un teléfono' }]}>
                                <Input type='number' placeholder="Teléfono"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="email" rules={[{ required: true, message: 'Por favor, ingrese un email' }]}>
                                <Input placeholder="Email"/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row center="xs">
                        <Form.Item name="cuil" rules={[{ required: true, message: 'Por favor, ingrese CUIL/CUIT' }]}>
                            <Input type='number' placeholder="CUIL/CUIT"/>
                        </Form.Item>
                    </Row>
                    <Row center="xs">
                        <Button type="primary" htmlType="submit">Guardar</Button>
                    </Row>
                </Form>
            </Modal>
            <Modal title="Agregar cliente" visible={isModalClientVisible} onCancel={handleCancelClient} onOk={handleOkClient}>
                <Form form={modalFormClient} onFinish={handleFinishClient} onAbort={handleCancelClient}>
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Item name="name" rules={[{ required: true, message: 'Por favor, ingrese razón social' }]}>
                                <Input placeholder="Razón social"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="enabled" rules={[{ required: true, message: 'Por favor, ingrese rubro' }]}>
                                <Select placeholder="Habilitado">
                                    <Option key={true} value={true}>Si</Option>
                                    <Option key={false} value={false}>No</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="phone" rules={[{ required: true, message: 'Por favor, ingrese un teléfono' }]}>
                                <Input type='number' placeholder="Teléfono"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="email" rules={[{ required: true, message: 'Por favor, ingrese un email' }]}>
                                <Input placeholder="Email"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="cuil" rules={[{ required: true, message: 'Por favor, ingrese CUIL/CUIT' }]}>
                                <Input type='number' placeholder="CUIL/CUIT"/>
                            </Form.Item>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Item name="type">
                                <Row>
                                    <Col xs={4}>
                                        <p>Publico</p>
                                    </Col>
                                        <Col xs={4}>
                                            <Switch onChange={onChangeSwitch}/>
                                        </Col>
                                    <Col xs={4}>
                                        <p>Privado</p>
                                    </Col>
                                </Row>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                    <Col xs={12} md={6}>
                        <Form.Item name="interlocutor">
                            <Input placeholder="Interlocutor"/>
                        </Form.Item>
                    </Col>
                </Row>
                    <Row center="xs">
                        <Button type="primary" htmlType="submit">Guardar</Button>
                    </Row>
                </Form>
            </Modal>
        </Grid>
    )
})
FormBidOrder.propTypes = {
    loading: PropTypes.bool.isRequired,
    onSubmitBidOrder:PropTypes.func.isRequired,
    isEditing:PropTypes.bool,
    bidOrderToEdit: PropTypes.object,
    clients: PropTypes.object.isRequired,
}

export default FormBidOrder

